// src/pages/Profile.js
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import api from '../utils/api';
import { getUser, clearAuth } from '../utils/auth';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/NotificationContainer';
import Navbar from '../components/common/Navbar';
import ProfileHeader from '../components/profile/ProfileHeader';
import ProfileForm from '../components/profile/ProfileForm';
import AvatarUpload from '../components/profile/AvatarUpload';
import ChangePasswordForm from '../components/profile/ChangePasswordForm';
import ChangeEmailForm from '../components/profile/ChangeEmailForm';
import DeleteAccountForm from '../components/profile/DeleteAccountForm';
import UserPreferences from '../components/profile/UserPreferences';
import DisclaimerFooter from '../components/common/DisclaimerFooter';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('profile');
  const [editing, setEditing] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { setTheme: applyTheme } = useTheme();
  const { notifications, showSuccess, showError, removeNotification } = useNotification();
  const currentUser = getUser();
  const hasLoadedProfileRef = useRef(false); // Prevent duplicate profile loads

  // Set document direction on language change
  useEffect(() => {
    const lng = localStorage.getItem('language') || 'ar';
    document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lng;
  }, [i18n.language]);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    // Load profile only once
    if (!hasLoadedProfileRef.current) {
      hasLoadedProfileRef.current = true;
      loadProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]); // Removed navigate from dependencies

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/profile/me');
      setUser(response.data.user);
      setPreferences(response.data.user.preferences);
    } catch (err) {
      // Handle rate limit errors gracefully
      if (err.response?.status === 429) {
        showError('Too many requests. Please wait a moment and refresh the page.');
      } else {
        showError('Failed to load profile');
      }
      console.error('Profile load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (profileData) => {
    try {
      const response = await api.patch('/api/profile/me', profileData);
      setUser(response.data.user);
      setEditing(false);
      showSuccess('Profile updated successfully');
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to update profile';
      showError(errorMsg);
    }
  };

  const handleUploadAvatar = async (formData) => {
    try {
      setUploadingAvatar(true);
      const response = await api.post('/api/profile/me/avatar', formData, {});
      setUser(response.data.user);
      showSuccess('Avatar updated successfully');
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to upload avatar';
      showError(errorMsg);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleChangePassword = async (passwordData) => {
    try {
      await api.post('/api/profile/me/change-password', passwordData);
      setShowChangePassword(false);
      showSuccess('Password changed successfully');
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to change password';
      showError(errorMsg);
    }
  };

  const handleChangeEmail = async (emailData) => {
    try {
      await api.post('/api/profile/me/change-email', emailData);
      setShowChangeEmail(false);
      showSuccess('Verification email sent to new address. Please check your inbox.');
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to request email change';
      showError(errorMsg);
    }
  };

  const handleDeleteAccount = async (deleteData) => {
    try {
      if (deleteData.action === 'deactivate') {
        await api.post('/api/profile/me/request-delete', deleteData);
        showSuccess('Account deactivated successfully');
        clearAuth();
        navigate('/login');
      } else {
        await api.post('/api/profile/me/request-delete', deleteData);
        setShowDeleteAccount(false);
        showSuccess('Deletion confirmation email sent. Please check your inbox.');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to process account deletion';
      showError(errorMsg);
    }
  };

  const handleSavePreferences = async (prefsData) => {
    try {
      const response = await api.patch('/api/profile/me/preferences', prefsData);
      setPreferences(response.data.preferences);
      setShowPreferences(false);
      showSuccess(t('profile.preferences.saved') || 'Preferences saved successfully');
      
      // Apply theme preference
      if (prefsData.theme) {
        applyTheme(prefsData.theme);
        localStorage.setItem('theme', prefsData.theme);
      }
      
      // Apply language preference
      if (prefsData.language) {
        i18n.changeLanguage(prefsData.language);
        localStorage.setItem('language', prefsData.language);
        document.documentElement.dir = prefsData.language === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = prefsData.language;
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || t('profile.preferences.saveFailed') || 'Failed to save preferences';
      showError(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shayek-primary"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const sections = [
    { id: 'profile', label: t('profile.sections.profile') || 'Profile', icon: '👤' },
    { id: 'security', label: t('profile.sections.security') || 'Security', icon: '🔒' },
    { id: 'preferences', label: t('profile.sections.preferences') || 'Preferences', icon: '⚙️' },
    { id: 'account', label: t('profile.sections.account') || 'Account', icon: '🗑️' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-shayek-light-50 to-shayek-light-100 dark:from-shayek-dark dark:to-shayek-dark-50 transition-colors">
      <Navbar showHome={true} />
      <NotificationContainer 
        notifications={notifications} 
        onRemove={removeNotification} 
      />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-shayek-light-900 dark:text-shayek-dark-900 mb-2">
            {t('profile.title') || 'Profile Settings'}
          </h1>
          <p className="text-shayek-light-600 dark:text-shayek-dark-600">
            {t('profile.description') || 'Manage your account settings and preferences'}
          </p>
        </div>

        {/* Profile Header */}
        {!editing && !showChangePassword && !showChangeEmail && !showDeleteAccount && !showPreferences && (
          <div className="mb-6">
            <ProfileHeader user={user} onEdit={() => setEditing(true)} />
          </div>
        )}

        {/* Navigation Tabs */}
        {!editing && !showChangePassword && !showChangeEmail && !showDeleteAccount && !showPreferences && (
          <div className="mb-6 bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek-lg border border-shayek-light-300 dark:border-shayek-dark-200 p-2">
            <div className="flex space-x-2">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeSection === section.id
                      ? 'bg-shayek-primary dark:bg-shayek-primary-600 text-white'
                      : 'text-shayek-light-700 dark:text-shayek-dark-700 hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-50'
                  }`}
                >
                  <span className="mr-2">{section.icon}</span>
                  {section.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Content Sections */}
        <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek-lg border border-shayek-light-300 dark:border-shayek-dark-200 p-6">
          {/* Profile Edit */}
          {editing && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-shayek-light-900 dark:text-shayek-dark-900 mb-2">
                  Edit Profile
                </h2>
                <AvatarUpload
                  currentAvatar={user.avatar_url}
                  onUpload={handleUploadAvatar}
                  loading={uploadingAvatar}
                />
              </div>
              <ProfileForm
                user={user}
                onSave={handleSaveProfile}
                onCancel={() => setEditing(false)}
                loading={false}
              />
            </div>
          )}

          {/* Change Password */}
          {showChangePassword && (
            <div>
              <h2 className="text-2xl font-bold text-shayek-light-900 dark:text-shayek-dark-900 mb-6">
                Change Password
              </h2>
              <ChangePasswordForm
                onSave={handleChangePassword}
                onCancel={() => setShowChangePassword(false)}
                loading={false}
              />
            </div>
          )}

          {/* Change Email */}
          {showChangeEmail && (
            <div>
              <h2 className="text-2xl font-bold text-shayek-light-900 dark:text-shayek-dark-900 mb-6">
                Change Email Address
              </h2>
              <ChangeEmailForm
                currentEmail={user.email}
                onSave={handleChangeEmail}
                onCancel={() => setShowChangeEmail(false)}
                loading={false}
              />
            </div>
          )}

          {/* Delete Account */}
          {showDeleteAccount && (
            <div>
              <h2 className="text-2xl font-bold text-shayek-light-900 dark:text-shayek-dark-900 mb-6">
                Delete Account
              </h2>
              <DeleteAccountForm
                onSave={handleDeleteAccount}
                onCancel={() => setShowDeleteAccount(false)}
                loading={false}
              />
            </div>
          )}

          {/* Preferences */}
          {showPreferences && (
            <div>
              <h2 className="text-2xl font-bold text-shayek-light-900 dark:text-shayek-dark-900 mb-6">
                {t('profile.preferences.title') || 'Preferences'}
              </h2>
              <div className="bg-white dark:bg-shayek-dark-50 p-6 rounded-lg border border-shayek-light-300 dark:border-shayek-dark-200">
                <UserPreferences
                  preferences={preferences}
                  onSave={handleSavePreferences}
                  onCancel={() => setShowPreferences(false)}
                  loading={false}
                />
              </div>
            </div>
          )}

          {/* Default View - Section Content */}
          {!editing && !showChangePassword && !showChangeEmail && !showDeleteAccount && !showPreferences && (
            <div>
              {activeSection === 'profile' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">
                      Profile Information
                    </h2>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-shayek-light-600 dark:text-shayek-dark-600 mb-1">
                          Name
                        </label>
                        <p className="text-shayek-light-900 dark:text-shayek-dark-900">
                          {user.name || 'Not set'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-shayek-light-600 dark:text-shayek-dark-600 mb-1">
                          Email
                        </label>
                        <p className="text-shayek-light-900 dark:text-shayek-dark-900">
                          {user.email}
                        </p>
                      </div>
                      {user.bio && (
                        <div>
                          <label className="block text-sm font-medium text-shayek-light-600 dark:text-shayek-dark-600 mb-1">
                            Bio
                          </label>
                          <p className="text-shayek-light-900 dark:text-shayek-dark-900">
                            {user.bio}
                          </p>
                        </div>
                      )}
                      {user.phone && (
                        <div>
                          <label className="block text-sm font-medium text-shayek-light-600 dark:text-shayek-dark-600 mb-1">
                            Phone
                          </label>
                          <p className="text-shayek-light-900 dark:text-shayek-dark-900">
                            {user.phone}
                          </p>
                        </div>
                      )}
                      {user.location && (
                        <div>
                          <label className="block text-sm font-medium text-shayek-light-600 dark:text-shayek-dark-600 mb-1">
                            Location
                          </label>
                          <p className="text-shayek-light-900 dark:text-shayek-dark-900">
                            {user.location}
                          </p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-shayek-light-600 dark:text-shayek-dark-600 mb-1">
                          Member Since
                        </label>
                        <p className="text-shayek-light-900 dark:text-shayek-dark-900">
                          {new Date(user.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      {user.last_login && (
                        <div>
                          <label className="block text-sm font-medium text-shayek-light-600 dark:text-shayek-dark-600 mb-1">
                            Last Login
                          </label>
                          <p className="text-shayek-light-900 dark:text-shayek-dark-900">
                            {new Date(user.last_login).toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'security' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">
                    Security Settings
                  </h2>
                  <div className="space-y-4">
                    <div className="p-4 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg">
                      <h3 className="font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-2">
                        {t('profile.security.changePassword') || 'Change Password'}
                      </h3>
                      <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600 mb-4">
                        {t('profile.security.changePasswordDesc') || 'Update your password to keep your account secure.'}
                      </p>
                      <button
                        onClick={() => setShowChangePassword(true)}
                        className="px-4 py-2 bg-shayek-primary dark:bg-shayek-primary-600 text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 transition-colors"
                      >
                        {t('profile.security.changePassword') || 'Change Password'}
                      </button>
                    </div>
                    <div className="p-4 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50">
                      <h3 className="font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-2">
                        {t('profile.security.changeEmail') || 'Change Email Address'}
                      </h3>
                      <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600 mb-4">
                        {t('profile.security.changeEmailDesc') || "Change your email address. You'll need to verify the new email."}
                      </p>
                      <button
                        onClick={() => setShowChangeEmail(true)}
                        className="px-4 py-2 bg-shayek-primary dark:bg-shayek-primary-600 text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 transition-colors"
                      >
                        {t('profile.security.changeEmail') || 'Change Email'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'preferences' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">
                    {t('profile.preferences.title') || 'Preferences'}
                  </h2>
                  <div className="p-4 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-shayek-light-600 dark:text-shayek-dark-600 mb-1">
                          {t('profile.preferences.theme') || 'Theme'}
                        </label>
                        <p className="text-shayek-light-900 dark:text-shayek-dark-900 capitalize">
                          {preferences?.theme === 'system' ? (t('profile.preferences.systemDefault') || 'system') : 
                           preferences?.theme === 'light' ? (t('profile.preferences.light') || 'light') :
                           preferences?.theme === 'dark' ? (t('profile.preferences.dark') || 'dark') : 
                           (preferences?.theme || 'system')}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-shayek-light-600 dark:text-shayek-dark-600 mb-1">
                          {t('profile.preferences.language') || 'Language'}
                        </label>
                        <p className="text-shayek-light-900 dark:text-shayek-dark-900">
                          {preferences?.language === 'ar' ? (t('profile.preferences.arabic') || 'العربية (Arabic)') : (t('profile.preferences.english') || 'English')}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-shayek-light-600 dark:text-shayek-dark-600 mb-1">
                          {t('profile.preferences.emailNotifications') || 'Email Notifications'}
                        </label>
                        <p className="text-shayek-light-900 dark:text-shayek-dark-900">
                          {preferences?.email_notifications !== false ? (t('profile.preferences.enabled') || 'Enabled') : (t('profile.preferences.disabled') || 'Disabled')}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowPreferences(true)}
                      className="mt-4 px-4 py-2 bg-shayek-primary dark:bg-shayek-primary-600 text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 transition-colors"
                    >
                      {t('profile.preferences.edit') || 'Edit Preferences'}
                    </button>
                  </div>
                </div>
              )}

              {activeSection === 'account' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">
                    Account Management
                  </h2>
                  <div className="p-4 border-2 border-red-300 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-900/20">
                    <h3 className="font-semibold text-red-800 dark:text-red-300 mb-2">
                      Danger Zone
                    </h3>
                    <p className="text-sm text-red-700 dark:text-red-400 mb-4">
                      Deactivate or permanently delete your account. These actions cannot be easily undone.
                    </p>
                    <button
                      onClick={() => setShowDeleteAccount(true)}
                      className="px-4 py-2 bg-red-600 dark:bg-red-700 text-white rounded-lg hover:bg-red-700 dark:hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                    >
                      Manage Account Deletion
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <DisclaimerFooter />
    </div>
  );
};

export default Profile;

